// src/app/pages/user/GroupTransferList.js
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { inboxPage } from '../_redux/actions/inboxActions';
import CloneGroupsPanel from './CloneGroupsPanel';

const GroupTransferList = (props) => {
    const { updateState, validationError, newUser } = props;
    const bigArrayGroup = Array.isArray(props.bigArrayGroup)
        ? props.bigArrayGroup
        : [];
    const groupArray = Array.isArray(props.groupArray) ? props.groupArray : [];
    const dispatch = useDispatch();
    const editSignaled = useRef(false);

    const [assigned, setAssignedState] = useState(() =>
        newUser ? [] : groupArray || []
    );
    const [availableFilter, setAvailableFilter] = useState('');
    const [assignedFilter, setAssignedFilter] = useState('');
    const [checkedAvailable, setCheckedAvailable] = useState(new Set());
    const [checkedAssigned, setCheckedAssigned] = useState(new Set());
    const [showClone, setShowClone] = useState(false);

    useEffect(() => {
        if (newUser) {
            dispatch(inboxPage({ updateGroupArray: [] }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const prevGroupArrayRef = useRef(groupArray);
    useEffect(() => {
        if (!newUser && prevGroupArrayRef.current !== groupArray) {
            prevGroupArrayRef.current = groupArray;
            setAssignedState(groupArray || []);
            setCheckedAvailable(new Set());
            setCheckedAssigned(new Set());
            editSignaled.current = false;
        }
    }, [groupArray, newUser]);

    const applyAssigned = (newAssigned) => {
        if (!editSignaled.current) {
            editSignaled.current = true;
            updateState && updateState({ editGroup: true });
        }
        setAssignedState(newAssigned);
        setCheckedAvailable(new Set());
        setCheckedAssigned(new Set());
        dispatch(inboxPage({ updateGroupArray: newAssigned }));
    };

    const availableGroups = bigArrayGroup
        .filter((g) => !assigned.includes(g.name))
        .filter((g) =>
            g.name.toLowerCase().includes(availableFilter.toLowerCase())
        );

    const assignedFiltered = assigned.filter((name) =>
        name.toLowerCase().includes(assignedFilter.toLowerCase())
    );

    const toggleAvailable = (name) => {
        const next = new Set(checkedAvailable);
        next.has(name) ? next.delete(name) : next.add(name);
        setCheckedAvailable(next);
    };

    const toggleAssigned = (name) => {
        const next = new Set(checkedAssigned);
        next.has(name) ? next.delete(name) : next.add(name);
        setCheckedAssigned(next);
    };

    const moveSelected = () => {
        const toAdd = availableGroups
            .filter((g) => checkedAvailable.has(g.name))
            .map((g) => g.name);
        if (toAdd.length === 0) return;
        applyAssigned([...assigned, ...toAdd]);
    };

    const moveAll = () => {
        const toAdd = availableGroups.map((g) => g.name);
        if (toAdd.length === 0) return;
        applyAssigned([...assigned, ...toAdd]);
    };

    const removeSelected = () => {
        const toRemove = new Set(
            assignedFiltered.filter((name) => checkedAssigned.has(name))
        );
        if (toRemove.size === 0) return;
        applyAssigned(assigned.filter((name) => !toRemove.has(name)));
    };

    const removeAll = () => {
        const visible = new Set(assignedFiltered);
        applyAssigned(assigned.filter((name) => !visible.has(name)));
    };

    const clearAllAssigned = () => {
        if (assigned.length === 0) return;
        applyAssigned([]);
    };

    const hasError = validationError && assigned.length === 0;

    if (showClone) {
        return (
            <div className='group-transfer-list'>
                <div className='gtl-body'>
                    <CloneGroupsPanel
                        currentAssigned={assigned}
                        onApply={(newAssigned) => {
                            applyAssigned(newAssigned);
                            setShowClone(false);
                        }}
                        onCancel={() => setShowClone(false)}
                    />
                </div>
            </div>
        );
    }

    return (
        <div className='group-transfer-list'>
            <div className='gtl-body'>
                <div className='gtl-toolbar'>
                    <button
                        type='button'
                        className='btn btn-sm'
                        onClick={() => setShowClone(true)}
                        title='Copy group assignments from another user'
                    >
                        <i className='fa-light fa-user-group' />
                        Clone From User&hellip;
                    </button>
                    <button
                        type='button'
                        className='btn btn-sm gtl-btn-danger'
                        onClick={clearAllAssigned}
                        disabled={assigned.length === 0}
                        title='Remove all assigned groups'
                    >
                        <i className='fa-light fa-xmark' />
                        Remove All
                    </button>
                </div>

                <div className='gtl-columns'>
                    <div className='gtl-col'>
                        <div className='gtl-col-header'>
                            <small>Available Groups</small>
                            <span className='gtl-count gtl-count-available'>
                                {availableGroups.length}
                            </span>
                        </div>
                        <input
                            type='text'
                            className='form-control form-control-sm mb-1'
                            placeholder='search available...'
                            value={availableFilter}
                            onChange={(e) => setAvailableFilter(e.target.value)}
                        />
                        <div className='gtl-list'>
                            {availableGroups.map((g) => (
                                <div
                                    key={g.name}
                                    className={`gtl-row${
                                        checkedAvailable.has(g.name)
                                            ? ' gtl-checked'
                                            : ''
                                    }`}
                                    onClick={() => toggleAvailable(g.name)}
                                    title={g.name}
                                >
                                    <input
                                        type='checkbox'
                                        checked={checkedAvailable.has(g.name)}
                                        readOnly
                                    />
                                    <span
                                        className='gtl-name'
                                        style={{ color: '#000' }}
                                    >
                                        {g.name}
                                    </span>
                                </div>
                            ))}
                            {availableGroups.length === 0 && (
                                <div className='gtl-empty'>
                                    {availableFilter
                                        ? 'No matches'
                                        : 'All groups assigned'}
                                </div>
                            )}
                        </div>
                    </div>

                    <div className='gtl-arrows'>
                        <button
                            type='button'
                            className='btn btn-primary btn-sm gtl-arrow'
                            onClick={moveAll}
                            data-label='Add all visible'
                        >
                            <i className='fa-solid fa-angles-right' />
                        </button>
                        <button
                            type='button'
                            className='btn btn-primary btn-sm gtl-arrow'
                            onClick={moveSelected}
                            data-label='Add selected'
                        >
                            <i className='fa-solid fa-angle-right' />
                        </button>
                        <button
                            type='button'
                            className='btn btn-secondary btn-sm gtl-arrow'
                            onClick={removeSelected}
                            data-label='Remove selected'
                        >
                            <i className='fa-solid fa-angle-left' />
                        </button>
                        <button
                            type='button'
                            className='btn btn-secondary btn-sm gtl-arrow'
                            onClick={removeAll}
                            data-label='Remove all visible'
                        >
                            <i className='fa-solid fa-angles-left' />
                        </button>
                    </div>

                    <div className='gtl-col'>
                        <div className='gtl-col-header'>
                            <small>Assigned Groups</small>
                            <span className='gtl-count gtl-count-assigned'>
                                {assigned.length}
                            </span>
                        </div>
                        <input
                            type='text'
                            className='form-control form-control-sm mb-1'
                            placeholder='search assigned...'
                            value={assignedFilter}
                            onChange={(e) => setAssignedFilter(e.target.value)}
                        />
                        <div
                            className={`gtl-list${
                                hasError ? ' has-error' : ''
                            }`}
                        >
                            {assignedFiltered.map((name) => (
                                <div
                                    key={name}
                                    className={`gtl-row${
                                        checkedAssigned.has(name)
                                            ? ' gtl-checked'
                                            : ''
                                    }`}
                                    onClick={() => toggleAssigned(name)}
                                    title={name}
                                >
                                    <input
                                        type='checkbox'
                                        checked={checkedAssigned.has(name)}
                                        readOnly
                                    />
                                    <span
                                        className='gtl-name'
                                        style={{ color: '#000' }}
                                    >
                                        {name}
                                    </span>
                                </div>
                            ))}
                            {assignedFiltered.length === 0 && (
                                <div className='gtl-empty'>
                                    {assignedFilter
                                        ? 'No matches'
                                        : 'No groups assigned'}
                                </div>
                            )}
                        </div>
                        {hasError && (
                            <p className='gtl-error'>
                                At least one group is required.
                            </p>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default GroupTransferList;
